import React, { useEffect, useRef, useState, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';

import api from '@api/api';
import createVerifier from '@models/dashboardRules';
import {
  IDashboardLayoutProps,
  IInstantEnergyDispatchSecurity,
} from '@models/home';
import DashboardAreaChart from '@pages/Home/components/DashboardAreaChart/DashboardAreaChart';
import DashboardDonutChart from '@pages/Home/components/DashboardDonutChart/DashboardDonutChart';
import MetricCard from '@pages/Home/components/MetricCard/MetricCard';
import TreeGraph from '@pages/Home/components/TreeGraph/TreeGraph';
import dataFlowColors from '@pages/Home/models/dataFlowColors';
import { NODES } from '@pages/Home/models/treeGraph';
import { AppDispatch } from '@store/index';
import { IRootState } from '@store/reducers';
import { setEnergyDispatchSecurity } from '@store/reducers/dashboardRealTimeValueSlice';
import { formatNumberWithDigits } from '@utils/common';
import { App } from 'antd';
import axios from 'axios';

import '../BaseLayout.scss'; // 這邊是 Layout 共用的 css

interface Panel {
  chargingPileCount?: number;
  chargingCurrent?: number;
  totalPileCount?: number;
  currentLimit?: number;
}

interface Contract {
  [key: string]: Panel | number | string;
  contractedEnergy: number; // Fix: Change from 'number | undefined' to 'number'
  chargingPower: number; // Fix: Change from 'number | undefined' to 'number'
}

interface Data {
  [key: string]: Contract;
}

const initInstantMdbData = {
  instantSellerPower: {
    chargingPower: undefined,
    contractedEnergy: undefined,
  },
  instantSellerCurrent: {
    chargingCurrent: undefined,
    currentLimit: undefined,
  },
  instantSellerPile: {
    totalPileCount: undefined,
    chargingPileCount: undefined,
  },
};

interface ILongTermData {
  powerTrendData: {
    timeList: string[];
    powers: string[] | number[];
  };
  carbonOmitTrendData: {
    timeList: string[];
    powers: string[];
    carbonEmissionFactors: number[];
    carbonEmissions: number[];
  };
}

const ShortTermGraph = memo(() => (
  <TreeGraph
    connections={[
      // 契約容量能源功率 #B877f9
      // 分電盤電流使用率 #2068F6
      // 充電樁能源啟動率 #6FD29F
      // 能源消耗功率趨勢 #F7C55D /no
      // 碳排放趨勢 #E4E7EC /no
      {
        data: [NODES.POWER_GRID(950, 80), NODES.STATION(800, 350)],
        direction: 'left',
        color: [dataFlowColors.ContractCapacityEnergyPower],
      },
      {
        data: [NODES.STATION(800, 350), NODES.DISTRIBUTOR(550, 100)],
        direction: 'left',
        color: [dataFlowColors.ContractCapacityEnergyPower],
      },
      {
        data: [NODES.DISTRIBUTOR(550, 100), NODES.PILE(430, 350)],
        direction: 'left',
        color: [
          dataFlowColors.DistributionBoardCurrentUsageRate,
          dataFlowColors.ChargingPileEnergyStartRate,
        ],
      },
    ]}
  />
));

const LongTermGraph = memo(() => (
  <TreeGraph
    connections={[
      // 契約容量能源功率 #B877f9 /n
      // 分電盤電流使用率 #2068F6 /n
      // 充電樁能源啟動率 #6FD29F /n
      // 能源消耗功率趨勢 #F7C55D /y
      // 碳排放趨勢 #E4E7EC /y
      {
        data: [NODES.POWER_GRID(950, 80), NODES.STATION(1030, 400)],
        direction: 'right',
        color: [
          dataFlowColors.EnergyConsumptionTrend,
          dataFlowColors.CarbonEmissionTrend,
        ],
      },
      {
        data: [NODES.STATION(1030, 400), NODES.DISTRIBUTOR(640, 160)],
        direction: 'left',
        color: [
          dataFlowColors.EnergyConsumptionTrend,
          dataFlowColors.CarbonEmissionTrend,
        ],
      },
      {
        data: [NODES.DISTRIBUTOR(640, 160), NODES.PILE(580, 350)],
        direction: 'left',
        color: [
          dataFlowColors.EnergyConsumptionTrend,
          dataFlowColors.CarbonEmissionTrend,
        ],
      },
      {
        data: [NODES.PILE(580, 350), NODES.EV(450, 490)],
        direction: 'left',
        color: [
          dataFlowColors.EnergyConsumptionTrend,
          dataFlowColors.CarbonEmissionTrend,
        ],
      },
    ]}
  />
));

function EnergyDispatchSecurity({
  activeTab, // 現在選擇的 tab
  activeLongTermOption, // 長期資訊中的時間區間
}: IDashboardLayoutProps) {
  const { message } = App.useApp();
  const { t } = useTranslation();
  // 該 layout 的 state，如用於存取對應 x, y 的 state，可以是單一值或物件，看 <TreeGraph /> props 要求
  const [rawMDBData, setRawMDBData] = useState<any>(null);
  const [instantMdbData, setInstantMdbData] =
    useState<IInstantEnergyDispatchSecurity>(initInstantMdbData);
  const [longTermData, setLongTermData] = useState<ILongTermData | null>(null);
  const timeoutIdRef = useRef<NodeJS.Timeout | boolean | null>(null);
  const controllersRef = useRef([axios.CancelToken.source()]);
  // 創建驗證器的 ref
  const commonVerifierRef = useRef(createVerifier());
  const pileCountVerifierRef = useRef(createVerifier());
  const sellerId = useSelector((state: IRootState) => state.userSlice.sellerId);
  const basicInfoPileCount = useSelector(
    (state: IRootState) =>
      state.dashboardRealTimeValueSlice.BasicInfo.pileCount,
  );
  const dispatch = useDispatch<AppDispatch>();

  // fetch function
  // TODO: Instant MDB fetch data
  async function fetchInstantMDBData() {
    timeoutIdRef.current = true;
    try {
      const controllers = [axios.CancelToken.source()];
      controllersRef.current = controllers;

      const mdbData = await api.post(
        '/api/statistics-service/operationView/getSellerEnergyDispatch',
        {
          sellerId,
          stationId: 0,
          timeSharing: 'TODAY',
        },
      );
      const {
        data: { data, code },
      } = mdbData;

      if (code !== 0) {
        console.error('An error occurred while fetching sellerInfo');
        return;
      }

      setRawMDBData(data);
    } catch (error) {
      console.error('fetchInstantMDBData error:', error);
    }
  }

  async function recursivelyFetchInstantMDBData() {
    if (!timeoutIdRef.current) return;

    timeoutIdRef.current = setTimeout(async () => {
      await fetchInstantMDBData();
      recursivelyFetchInstantMDBData();
    }, 5000);
  }

  // TODO: normalizing Instant MDB data
  function getInstantMDBData(data: Data) {
    if (!data) return;

    const instantSellerPower = { chargingPower: 0, contractedEnergy: 0 };
    const instantSellerCurrent = { chargingCurrent: 0, currentLimit: 0 };
    const instantSellerPile = { totalPileCount: 0, chargingPileCount: 0 };

    if (data && typeof data === 'object') {
      Object.values(data).forEach((contract) => {
        if (contract && typeof contract === 'object') {
          if (
            'contractedEnergy' in contract &&
            typeof contract.contractedEnergy === 'number'
          ) {
            instantSellerPower.contractedEnergy += contract.contractedEnergy;
          }
          if ('chargingPower' in contract) {
            const chargingPower = parseFloat(String(contract.chargingPower));
            if (!Number.isNaN(chargingPower)) {
              instantSellerPower.chargingPower += chargingPower;
            }
          }
          Object.values(contract).forEach((panel) => {
            if (panel && typeof panel === 'object') {
              if (
                'chargingCurrent' in panel &&
                typeof panel.chargingCurrent === 'number'
              ) {
                instantSellerCurrent.chargingCurrent += panel.chargingCurrent;
              }
              if (
                'currentLimit' in panel &&
                typeof panel.currentLimit === 'number'
              ) {
                instantSellerCurrent.currentLimit += panel.currentLimit;
              }
              if (
                'totalPileCount' in panel &&
                typeof panel.totalPileCount === 'number'
              ) {
                instantSellerPile.totalPileCount += panel.totalPileCount;
              }
              if (
                'chargingPileCount' in panel &&
                typeof panel.chargingPileCount === 'number'
              ) {
                instantSellerPile.chargingPileCount += panel.chargingPileCount;
              }
            }
          });
        }
      });
    }

    setInstantMdbData({
      instantSellerPower,
      instantSellerCurrent,
      instantSellerPile,
    });

    // save to store
    const { chargingPower, contractedEnergy } = instantSellerPower;
    const { chargingCurrent, currentLimit } = instantSellerCurrent;
    const { totalPileCount, chargingPileCount } = instantSellerPile;

    dispatch(
      setEnergyDispatchSecurity({
        instantSellerPower: {
          chargingPower,
          contractedEnergy,
        },
        instantSellerCurrent: {
          chargingCurrent,
          currentLimit,
        },
        instantSellerPile: {
          totalPileCount,
          chargingPileCount,
        },
      }),
    );

    commonVerifierRef.current(
      [
        'numerator-and-denominator-cannot-be-negative',
        'numerator-cannot-be-greater-than-denominator',
      ],
      [
        '/api/statistics-service/operationView/getSellerEnergyDispatch',
        '/api/statistics-service/operationView/getSellerEnergyDispatch',
        '/api/statistics-service/operationView/getSellerEnergyDispatch',
      ],
      [
        [chargingPower, contractedEnergy],
        [chargingCurrent, currentLimit],
        [chargingPileCount, totalPileCount],
      ],
      [
        ['chargingPower', 'contractedEnergy'],
        ['chargingCurrent', 'currentLimit'],
        ['chargingPileCount', 'totalPileCount'],
      ],
    );

    // 驗證充電樁數量不能小於基礎資料總數
    if (basicInfoPileCount !== undefined && totalPileCount !== undefined) {
      pileCountVerifierRef.current(
        ['totalPileCount-cannot-be-greater-than-basicInfoPileCount'],
        [
          [
            'api/statistics-service/operationView/getSellerEnergyDispatch',
            '/api/statistics-service/operationView/getStationfacilitiesCountsBySeller',
          ],
        ],
        [[totalPileCount, basicInfoPileCount]],
        [['totalPileCount', 'basicInfoPileCount']],
      );
    }
  }

  useEffect(() => {
    if (activeTab === 'longTerm' || activeTab === null) return;
    fetchInstantMDBData();
    recursivelyFetchInstantMDBData();

    // eslint-disable-next-line consistent-return
    return () => {
      if (timeoutIdRef.current) {
        if (
          typeof timeoutIdRef.current === 'number' ||
          typeof timeoutIdRef.current === 'object'
        ) {
          clearTimeout(timeoutIdRef.current);
          timeoutIdRef.current = null;
        }
      }
      controllersRef.current.forEach((controller) => {
        controller.cancel('cancel request');
      });
    };
  }, [activeTab]);

  useEffect(() => getInstantMDBData(rawMDBData), [rawMDBData]);

  // TODO: Long-term fetch data
  async function fetchPowerTrend(timeSharing: string) {
    try {
      const powerTrendData = await api.post(
        '/api/statistics-service/operationView/getSellerPowerStatistics',
        {
          sellerId,
          stationId: 0,
          timeSharing,
        },
      );
      const {
        data: { data, code },
      } = powerTrendData;

      if (code !== 0) {
        console.error('An error occurred while fetching sellerInfo');
        return;
      }

      setLongTermData((prevLongTermData: any) => ({
        ...prevLongTermData,
        powerTrendData: data,
      }));
    } catch (error) {
      console.error('fetchPowerTrend error:', error);
    }
  }

  async function fetchCarbonOmitTrend(timeSharing: string) {
    try {
      const carbonOmitTrendData = await api.post(
        '/api/statistics-service/operationView/getSellerCarbonOmit',
        {
          sellerId,
          stationId: 0,
          timeSharing,
        },
      );
      const {
        data: { data, code },
      } = carbonOmitTrendData;

      if (code !== 0) {
        console.error('An error occurred while fetching sellerInfo');
        return;
      }

      setLongTermData((prevLongTermData: any) => ({
        ...prevLongTermData,
        carbonOmitTrendData: data,
      }));
    } catch (error) {
      console.error('fetchCarbonOmitTrend error:', error);
    }
  }

  async function fetchLongTermData(timeSharing: string) {
    await fetchPowerTrend(timeSharing);
    await fetchCarbonOmitTrend(timeSharing);
  }

  useEffect(() => {
    if (activeTab === 'longTerm') {
      fetchLongTermData(activeLongTermOption);
    }
  }, [activeTab, activeLongTermOption]);

  useEffect(() => {
    if (!longTermData || !longTermData.carbonOmitTrendData) return;
    if (!longTermData.carbonOmitTrendData.carbonEmissionFactors.length) {
      message.error(t('EnergyDispatchSecurity.FactorNotSetOrError'));
    }
  }, [longTermData?.carbonOmitTrendData?.carbonEmissionFactors]);

  return (
    <div className="BaseLayout">
      {activeTab === 'immediate' && (
        <>
          <ShortTermGraph />
          <MetricCard
            title={t('EnergyDispatchSecurity.ContractCapacityEnergyPower')}
            leftTextArea={{
              subtitle: t('EnergyDispatchSecurity.InstantPowerTransmission'),
              value:
                instantMdbData.instantSellerPower.chargingPower === undefined
                  ? undefined
                  : formatNumberWithDigits(
                      instantMdbData.instantSellerPower.chargingPower,
                      0,
                    ),
            }}
            rightTextArea={{
              subtitle: t('EnergyDispatchSecurity.TotalRatedPower'),
              value:
                instantMdbData.instantSellerPower.contractedEnergy === undefined
                  ? undefined
                  : formatNumberWithDigits(
                      instantMdbData.instantSellerPower.contractedEnergy,
                      0,
                    ),
            }}
            tooltipContent={t(
              'EnergyDispatchSecurity.PowerInTransmissionDividedByTotal',
            )}
            position={{ left: '53%', top: '60%' }}
            ringColor={dataFlowColors.ContractCapacityEnergyPower}
          >
            <DashboardDonutChart
              title={t('EnergyDispatchSecurity.ContractCapacityEnergyPower')}
              value={
                instantMdbData.instantSellerPower.contractedEnergy ===
                  undefined ||
                instantMdbData.instantSellerPower.chargingPower === undefined
                  ? undefined
                  : ((!Number.isNaN(
                      instantMdbData.instantSellerPower.chargingPower,
                    )
                      ? instantMdbData.instantSellerPower.chargingPower
                      : 0) /
                      instantMdbData.instantSellerPower.contractedEnergy) *
                    100
              }
            />
          </MetricCard>
          <MetricCard
            title={t('EnergyDispatchSecurity.BoardCurrentUsageRate')}
            leftTextArea={{
              subtitle: t('EnergyDispatchSecurity.ChargingCurrent'),
              value:
                instantMdbData.instantSellerCurrent.chargingCurrent ===
                undefined
                  ? undefined
                  : formatNumberWithDigits(
                      instantMdbData.instantSellerCurrent.chargingCurrent,
                      0,
                    ),
            }}
            rightTextArea={{
              subtitle: t('EnergyDispatchSecurity.CurrentLimit'),
              value:
                instantMdbData.instantSellerCurrent.currentLimit === undefined
                  ? undefined
                  : formatNumberWithDigits(
                      instantMdbData.instantSellerCurrent.currentLimit,
                      0,
                    ),
            }}
            tooltipContent={t(
              'EnergyDispatchSecurity.CurrentInTransmissionDividedTotalRatedCurrent',
            )}
            position={{ left: '7%', top: '20%' }}
            ringColor={dataFlowColors.DistributionBoardCurrentUsageRate}
          >
            <DashboardDonutChart
              title={t('EnergyDispatchSecurity.BoardCurrentUsageRate')}
              value={
                instantMdbData.instantSellerCurrent.currentLimit ===
                  undefined ||
                instantMdbData.instantSellerCurrent.chargingCurrent ===
                  undefined
                  ? undefined
                  : ((!Number.isNaN(
                      instantMdbData.instantSellerCurrent.chargingCurrent,
                    )
                      ? instantMdbData.instantSellerCurrent.chargingCurrent
                      : 0) /
                      instantMdbData.instantSellerCurrent.currentLimit) *
                    100
              }
            />
          </MetricCard>
          <MetricCard
            title={t('EnergyDispatchSecurity.PileEnergyStartRate')}
            leftTextArea={{
              subtitle: t(
                'EnergyDispatchSecurity.TotalEnergyTransmissionPiles',
              ),
              value:
                instantMdbData.instantSellerPile.chargingPileCount === undefined
                  ? undefined
                  : formatNumberWithDigits(
                      instantMdbData.instantSellerPile.chargingPileCount,
                      0,
                    ),
            }}
            rightTextArea={{
              subtitle: t('EnergyDispatchSecurity.TotalPileCount'),
              value:
                instantMdbData.instantSellerPile.totalPileCount === undefined
                  ? undefined
                  : formatNumberWithDigits(
                      instantMdbData.instantSellerPile.totalPileCount,
                      0,
                    ),
            }}
            tooltipContent={t(
              'EnergyDispatchSecurity.PilesInTransmissionDividedByTotalPiles',
            )}
            position={{ left: '7%', top: '60%' }}
            ringColor={dataFlowColors.ChargingPileEnergyStartRate}
          >
            <DashboardDonutChart
              title={t('EnergyDispatchSecurity.PileEnergyStartRate')}
              value={
                instantMdbData.instantSellerPile.chargingPileCount ===
                  undefined ||
                instantMdbData.instantSellerPile.totalPileCount === undefined
                  ? undefined
                  : ((!Number.isNaN(
                      instantMdbData.instantSellerPile.chargingPileCount,
                    )
                      ? instantMdbData.instantSellerPile.chargingPileCount
                      : 0) /
                      instantMdbData.instantSellerPile.totalPileCount) *
                    100
              }
            />
          </MetricCard>
        </>
      )}
      {activeTab === 'longTerm' && (
        <>
          <LongTermGraph />
          <MetricCard
            title={t('EnergyDispatchSecurity.EnergyConsumptionTrend')}
            tooltipContent={t(
              'EnergyDispatchSecurity.PowerConsumptionInPeriod',
            )}
            position={{ left: '10%', top: '60%' }}
            ringColor={dataFlowColors.EnergyConsumptionTrend}
            isLongterm
          >
            <DashboardAreaChart
              range={activeLongTermOption}
              quantifier="kWh"
              chartData={
                !longTermData || !longTermData.powerTrendData
                  ? undefined
                  : {
                      time: longTermData.powerTrendData.timeList,
                      dataset: [
                        {
                          name: t('EnergyDispatchSecurity.EnergyUsage'),
                          data: longTermData.powerTrendData.powers.map(
                            (power: string | number) =>
                              Math.round(Number(power)),
                          ),
                          color: '#507ACD',
                        },
                      ],
                    }
              }
            />
          </MetricCard>
          <MetricCard
            title={t('EnergyDispatchSecurity.CarbonEmissionTrend')}
            tooltipContent={t(
              'EnergyDispatchSecurity.CarbonEmissionInTimePeriod',
            )}
            position={{ left: '10%', top: '20%' }}
            ringColor={dataFlowColors.CarbonEmissionTrend}
            isLongterm
          >
            <DashboardAreaChart
              range={activeLongTermOption}
              quantifier="kgCO2e"
              yAxisScaleFactor={2}
              chartData={
                !longTermData ||
                !longTermData.carbonOmitTrendData ||
                !longTermData.carbonOmitTrendData.carbonEmissionFactors.length
                  ? undefined
                  : {
                      time: longTermData.carbonOmitTrendData.timeList,
                      dataset: [
                        {
                          name: t('EnergyDispatchSecurity.PowerCarbonEmission'),
                          data: longTermData.carbonOmitTrendData
                            .carbonEmissions,
                          color: '#507ACD',
                        },
                      ],
                    }
              }
            />
          </MetricCard>
        </>
      )}
    </div>
  );
}

export default EnergyDispatchSecurity;
